import React from 'react';
import styled, { css } from 'styled-components';
import { Shine } from 'components';

function Button(props) {
  const variant = props.$variant ?? 'contained';
  const {
    backgroundColor = variant === 'outlined' || variant === 'text'
      ? 'transparent'
      : 'var(--dark-blue)',
    changeColorWhenDisabled = false,
    textColor = variant === 'outlined' || variant === 'text'
      ? 'var(--dark-blue)'
      : '#fff',
    border = 'none',
    brightenWhenActive = true,
    disabled = false,
    shine = false,
    loading = false,
    iconPosition = 'left',
    icon,
    children,
    width,
    ...rest
  } = props;
  return (
    <Container
      disabled={disabled || loading}
      $changeColorWhenDisabled={changeColorWhenDisabled}
      $variant={variant}
      $loading={loading}
      $backgroundColor={backgroundColor}
      $textColor={textColor}
      $border={border}
      $icon={icon}
      $brightenWhenActive={brightenWhenActive}
      $children={children}
      {...rest}>
      <LoadingSpinner $loading={loading} className="loading-spinner" />
      <ButtonContent $loading={loading} className="button-content">
        {icon && iconPosition === 'left' && (
          <span className="icon icon-start">{icon}</span>
        )}
        {children && <span className="button-text">{children}</span>}
        {icon && iconPosition === 'right' && (
          <span className="icon icon-end">{icon}</span>
        )}
        {shine ? <Shine /> : null}
      </ButtonContent>
    </Container>
  );
}

const Container = styled.button`
  align-items: center;
  background-color: ${(props) => props.$backgroundColor};
  border: ${(props) => props.$border};
  border-radius: var(--border-radius-lg);
  color: ${(props) => props.$textColor};
  cursor: pointer;
  display: inline-flex;
  font-size: 1rem;
  font-weight: 600;
  gap: 0.5em;
  justify-content: center;
  overflow: hidden;
  padding: ${({ $icon, $children }) =>
    $icon && $children ? '0.65em 0.95em' : $icon ? 0 : '0.65em 2em'};
  position: relative;
  transition: all 0.15s ease;

  &:focus {
    outline: none;
  }

  &:active:not(:disabled) {
    transform: scale(0.97);
  }

  &:disabled {
    /* background-color: ${(props) => !props.$loading && '#ededed'};
    color: ${(props) => !props.$loading && '#c4c4c4'}; */
    cursor: default;
    opacity: ${({ $changeColorWhenDisabled }) =>
      $changeColorWhenDisabled ? 0.4 : 1};
  }

  ${({ $variant, $icon, $children }) =>
    ($variant === 'contained' || $variant === 'outlined') &&
    css`
      padding: ${$icon && $children
        ? '0.65em 0.95em'
        : $icon
          ? 0
          : '0.65em 2em'};

      &:hover:not(:disabled),
      &:focus-visible:not(:disabled) {
        outline: ${(props) =>
          props.$backgroundColor === 'transparent'
            ? `0.063em solid ${props.$textColor}`
            : `0.063em solid ${props.$backgroundColor}`};
        outline-offset: 0.125em;
      }
    `}

  ${({ $variant }) =>
    $variant === 'text' &&
    css`
      left: -0.95em;
      position: relative;

      &:hover:not(:disabled),
      &:focus-visible:not(:disabled) {
        background-color: rgba(128, 128, 128, 0.0675);
        border-radius: 2px;
      }
    `}

  ${({ $variant, $textColor }) =>
    $variant === 'outlined' &&
    css`
      background-color: transparent;
      border: 1px solid ${$textColor};
      color: ${$textColor};
    `}

    &:hover:not(:disabled),
    &:focus-visible:not(:disabled) {
    filter: ${(props) =>
      props.$brightenWhenActive ? 'brightness(1.225)' : 'none'};
  }
`;

const LoadingSpinner = styled.span`
  animation: spin 0.8s linear infinite;
  border: 2px solid ${(props) => props.$textColor};
  border-radius: 50%;
  border-top-color: transparent;
  height: 20px;
  opacity: ${(props) => (props.$loading ? 1 : 0)};
  position: absolute;
  width: 20px;
  z-index: 0;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const ButtonContent = styled.span`
  align-items: center;
  display: flex;
  gap: 0.5em;
  justify-content: center;
  opacity: ${(props) => (props.$loading ? 0 : 1)};
  z-index: 1;

  .icon {
    align-items: center;
    display: flex;
    justify-content: center;
    line-height: 0;
  }
`;

export default Button;
